import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyledLayout, StyledTrialBannerWrapper } from './Layout.styles';
import TopBar from '../atoms/TopBar';
import Header from '../atoms/Header';
import TrialBanner from '../atoms/Header/TrialBanner';
import { useLocation } from 'react-router-dom';
// Walkthrough disabled — see hooks/useDummyData.js
// import OnboardingTour from '../atoms/Storybook';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import CrowSetup from '../CrowSetup';
// Parked: the welcome video modal has no video yet. Restore both lines (here and
// in the render below) once the intro video exists.
// import WelcomeVideoModal from '../atoms/WelcomeVideoModal';
import SetupGuide from '../atoms/SetupGuide';
// Parked: the Demo data / Fresh start switch is hidden for now (see
// DEMO_MODE_ENABLED in Context/demoModeContext). Unwrap both to restore it.
// import DemoModeToggle from '../atoms/DemoModeToggle';
import { useDemoMode } from '../../Context/demoModeContext';

/**
 * The trial banner is switched off for everyone.
 *
 * Turned off rather than deleted: the banner, its dismiss-for-a-day cookie and
 * the `trial-banner-visible` body class that offsets the layout beneath it are
 * all still wired up and working, so putting it back is this one flag. Flip it
 * to true and the original behaviour returns — shown to anyone without an
 * active subscription, for the first seven days after signup.
 */
const TRIAL_BANNER_ENABLED = false;

const TRIAL_BANNER_HIDE_COOKIE = 'houdiny_trial_banner_hidden_until';
const TRIAL_BANNER_HIDE_MS = 24 * 60 * 60 * 1000;
const TRIAL_BANNER_DURATION_DAYS = 7;

const getCookieValue = name => {
  const cookieEntry = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));
  return cookieEntry ? decodeURIComponent(cookieEntry.split('=')[1]) : null;
};

const setCookieValue = (name, value, maxAgeSeconds) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
};

const Layout = ({ children, showTemplate }) => {
  const location = useLocation();
  // Pages that render their own page-level chrome and so hide the contextual Header.
  // One flag drives both the render and the layout's top offset, so they cannot drift.
  const showHeader = !(
    location.pathname === '/unibox'
    || location.pathname.startsWith('/campaign-manager')
    || location.pathname === '/dashboard'
    || location.pathname === '/workforce'
    ||     location.pathname === '/ai-home'
    || location.pathname === '/analytics'
    || location.pathname === '/tasks'
    || location.pathname === '/email-accounts'
    || location.pathname === '/linkedin-accounts'
    // The offer pages carry their own back link and heading, the way the Remix
    // reference does — the contextual Header would duplicate both.
    || location.pathname.startsWith('/offers')
    || location.pathname === '/create-offer'
    // Team & Workspaces and Settings render the reference's own sub-header bar.
    || location.pathname === '/teams'
    || location.pathname === '/profile'
    || location.pathname === '/leads-module'
    || location.pathname === '/leads-list'
    || location.pathname.startsWith('/automation/')
    || location.pathname.includes('/flow-builder')
  );
  const crownEnabled = String(process.env.REACT_APP_CROW_ENABLED).toLowerCase() === 'true';
  const { user, loading_user, authReady } = useContextHook(AuthContext, v => ({
    user: v.user,
    loading_user: v.loading_user,
    authReady: v.authReady,
  }));
  const canRenderCrow = crownEnabled && user?.data?.featureAccess?.agentAccess;

  // ------- Trial banner (rendered globally so it shows on every page,
  // including ones where the Header is hidden: /unibox, /campaign-manager,
  // /automation/*). -------
  const [isTrialBannerVisible, setIsTrialBannerVisible] = useState(true);
  const trialBannerTimerRef = useRef(null);

  const syncTrialBannerVisibilityFromCookie = () => {
    const hiddenUntil = Number(getCookieValue(TRIAL_BANNER_HIDE_COOKIE));
    if (!hiddenUntil || Number.isNaN(hiddenUntil)) {
      setIsTrialBannerVisible(true);
      return;
    }

    const remainingMs = hiddenUntil - Date.now();
    if (remainingMs <= 0) {
      setIsTrialBannerVisible(true);
      return;
    }

    setIsTrialBannerVisible(false);
    if (trialBannerTimerRef.current) {
      clearTimeout(trialBannerTimerRef.current);
    }
    trialBannerTimerRef.current = setTimeout(() => {
      setIsTrialBannerVisible(true);
    }, remainingMs);
  };

  const isSubscriptionActive = user?.data?.status === 'subscription_active';
  const isTrialBannerExpired = useMemo(() => {
    const createdAt = user?.data?.created_at;
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return false;
    const elapsedMs = Date.now() - createdDate.getTime();
    const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
    return elapsedDays >= TRIAL_BANNER_DURATION_DAYS;
  }, [user?.data?.created_at]);

  const handleCloseTrialBanner = () => {
    const hiddenUntil = Date.now() + TRIAL_BANNER_HIDE_MS;
    setCookieValue(TRIAL_BANNER_HIDE_COOKIE, String(hiddenUntil), Math.ceil(TRIAL_BANNER_HIDE_MS / 1000));
    setIsTrialBannerVisible(false);
    if (trialBannerTimerRef.current) {
      clearTimeout(trialBannerTimerRef.current);
    }
    trialBannerTimerRef.current = setTimeout(() => {
      setIsTrialBannerVisible(true);
    }, TRIAL_BANNER_HIDE_MS);
  };

  const handleTrialBannerCloseStart = () => {
    document.body.classList.remove('trial-banner-visible');
  };

  useEffect(() => {
    syncTrialBannerVisibilityFromCookie();
    return () => {
      if (trialBannerTimerRef.current) {
        clearTimeout(trialBannerTimerRef.current);
      }
    };
  }, []);

  const isUserReadyForTrialBanner = authReady && !loading_user && Boolean(user?.data?.created_at);
  const shouldShowTrialBanner = Boolean(
    TRIAL_BANNER_ENABLED
    && showTemplate
    && isUserReadyForTrialBanner
    && isTrialBannerVisible
    && !isSubscriptionActive
    && !isTrialBannerExpired
    && location.pathname !== '/create-campaign',
  );

  useEffect(() => {
    const bodyClassName = 'trial-banner-visible';
    if (shouldShowTrialBanner) {
      document.body.classList.add(bodyClassName);
    } else {
      document.body.classList.remove(bodyClassName);
    }
    return () => {
      document.body.classList.remove(bodyClassName);
    };
  }, [shouldShowTrialBanner]);

  /*
   * Demo data is for looking at. Every click, mouse press (which would open a
   * select or focus a field), submit, paste and key press inside the page is
   * stopped before the page sees it, so nothing on screen does anything.
   * Scrolling still works (wheel, arrows, Page Up/Down, Tab to move focus), and
   * the top bar, navigation and the Preview switch live outside this area, so
   * the user can still move around and switch back. Settings stays usable.
   */
  const { demo } = useDemoMode();
  const onSettings = location.pathname === '/profile';
  const demoReadOnly = demo && !onSettings;
  const blockInDemo = e => {
    if (!demoReadOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', 'Tab'];
  const blockKeysInDemo = e => {
    if (!demoReadOnly || SCROLL_KEYS.includes(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      {/* Parked until the founder intro video exists: <WelcomeVideoModal active={location.pathname === '/ai-home'} /> */}
      {/* The setup checklist, on every app page once the welcome video is seen. */}
      {showTemplate && <SetupGuide />}
      {/* version3's Preview mode switch: demo data or the account's own. Not on
          Settings, which is about the account itself. */}
      {/* {showTemplate && !onSettings && <DemoModeToggle />} */}
      {shouldShowTrialBanner && (
        <StyledTrialBannerWrapper>
          <TrialBanner
            onClose={handleCloseTrialBanner}
            onCloseStart={handleTrialBannerCloseStart}
            user={user}
          />
        </StyledTrialBannerWrapper>
      )}
      <StyledLayout $showlayout={showTemplate} $hasHeader={showTemplate && showHeader}>
        <span className="overlay" />
        {showTemplate ? (
          <>
            {/* <OnboardingTour /> */}
            <TopBar />
            <div
              className={`children${location.pathname === '/unibox' ? ' unibox-layout' : ''}${
                demoReadOnly ? ' demo-readonly' : ''
              }`}
              onClickCapture={blockInDemo}
              onMouseDownCapture={blockInDemo}
              onDoubleClickCapture={blockInDemo}
              onSubmitCapture={blockInDemo}
              onPasteCapture={blockInDemo}
              onDropCapture={blockInDemo}
              onKeyDownCapture={blockKeysInDemo}>
              {showHeader && <Header />}
              {children}
              {canRenderCrow && (
                <CrowSetup />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="children">{children}
            </div>
          </>
        )}
      </StyledLayout>
    </>
  );
};

export default Layout;
